import React, { memo, useContext, useCallback } from 'react';
import type { NodeProps } from 'reactflow';
import { GraphViewContext } from '../GraphView';
import { BaseNode } from './BaseNode';

interface ActionButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  label: string;
  tooltip: string;
  variant?: 'default' | 'danger';
}

// Reusable button component with optional styling variants and tooltip support
const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, tooltip, variant = 'default' }) => (
  <button
    onClick={onClick}
    title={tooltip}
    className={`p-2 text-xs rounded transition-colors duration-150
      ${variant === 'danger'
        ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`
    }
  >
    {label}
  </button>
);

// React Flow node component for rendering a logical NOT operator in the policy graph
export const NotNode: React.FC<NodeProps> = memo(({ data, selected }) => {
  // Retrieve graph interaction handlers (e.g., toggle, delete) from context
  const handlers = useContext(GraphViewContext);
  if (!handlers) {
    console.error('[NotNode] Missing GraphViewContext');
    return null;
  }

  // Node path in the policy tree, used to identify which part to modify
  const path = data.path ?? [];

  // Callback to invoke the toggle NOT operation for this node path
  const toggleNot = useCallback(() => handlers.handleToggleNot(path), [handlers, path]);

  return (
    <BaseNode
      selected={selected}
      bgClasses="from-red-50 to-red-100 dark:from-red-800 dark:to-red-900"
      ringColor="ring-red-500"
      header="NOT"
      footer={<ActionButton onClick={toggleNot} label="🗑️" tooltip="Remove NOT" variant="danger" />}
    >
      {/* No extra content for NOT node */}
      <> </>
    </BaseNode>
  );
});

// Assign display name for clearer React DevTools debugging
NotNode.displayName = 'NotNode';
